/**
 * Reads a recipe before the cook opens it (#874).
 *
 * Opening a recipe waits for Firestore to answer for its steps. Each read
 * started here lands in `unifiedRecipeService`'s detail cache, and an open that
 * comes while it is still in flight joins it rather than asking again, so the
 * page draws from memory or from a read already halfway back. Three moments
 * say a recipe is about to be opened:
 *
 * - the planner's meal sheet or drawer opening, seconds before « Voir la recette »;
 * - the planner itself, for today's and tomorrow's meals, once boot is over
 *   (`usePrefetchPlannedRecipes`);
 * - the tap on a card, which starts the read before the recipe screen mounts.
 *
 * Never awaited and never reported: the open reads the recipe again if this
 * failed, and reports what it found.
 */

import { addDays } from '../utils/localDate';
import { auth, isFirebaseEnabled } from './firebaseConfig';
import personalRecipeDetails from './personalRecipeDetails';
import unifiedRecipeService from './unifiedRecipeService';

/** A recipe prefetched within this window is not asked for again. */
const RECENT_MS = 60_000;
const recent = new Map<string, number>();

/** Ids that name no recipe document. */
const NOT_A_RECIPE = new Set(['entry', 'free-text']);

export function prefetchRecipe(id: string | null | undefined, now: number = Date.now()): void {
  if (!id || NOT_A_RECIPE.has(id) || !isFirebaseEnabled()) return;
  const last = recent.get(id);
  if (last !== undefined && now - last < RECENT_MS) return;
  if (recent.size > 500) recent.clear();
  recent.set(id, now);

  // The bundled file too: the screen reads it first, and it is a fetch on web.
  personalRecipeDetails.getRecipeDetails(id).catch(() => null);
  unifiedRecipeService.getRecipeById(id, auth?.currentUser?.uid).catch(() => null);
}

/** For tests: forget what was prefetched. */
export function resetRecipePrefetch(): void {
  recent.clear();
}

interface WarmableMeal {
  recipeId: string;
  scheduledFor: string;
  mealType: string;
  mode?: 'proposed' | 'accepted';
}

/** Dinner is the meal the cook opens a recipe for; breakfast the least. */
const MEAL_ORDER: Record<string, number> = { dinner: 0, lunch: 1, breakfast: 2 };

/** Most a planner warms: a few reads a day, not one per planned meal. */
export const MAX_WARMED = 6;

/**
 * The recipes of today's and tomorrow's accepted meals, today first and
 * dinner first within a day, each once. A proposal is still being decided,
 * and an entry (« Repas simple », leftovers) has no recipe to read.
 */
export function plannedRecipesToWarm(meals: readonly WarmableMeal[], today: string): string[] {
  const tomorrow = addDays(today, 1);
  const days = [today, tomorrow];
  const ranked = meals
    .filter((meal) => days.includes(meal.scheduledFor))
    .filter((meal) => meal.mode !== 'proposed' && !!meal.recipeId && !NOT_A_RECIPE.has(meal.recipeId))
    .sort(
      (a, b) =>
        days.indexOf(a.scheduledFor) - days.indexOf(b.scheduledFor) ||
        (MEAL_ORDER[a.mealType] ?? 3) - (MEAL_ORDER[b.mealType] ?? 3),
    );
  return [...new Set(ranked.map((meal) => meal.recipeId))].slice(0, MAX_WARMED);
}
